import { useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

export const useSecureStorage = () => {
  const encryptData = useCallback(async (data: string): Promise<string> => {
    try {
      const { data: result, error } = await supabase.functions.invoke('secure-storage', {
        body: { action: 'encrypt', data }
      });
      
      if (error) throw error;
      return result.result;
    } catch (error) {
      console.error('Encryption failed:', error);
      return data; // Fallback to unencrypted
    }
  }, []);

  const decryptData = useCallback(async (encryptedData: string): Promise<string> => {
    try {
      const { data: result, error } = await supabase.functions.invoke('secure-storage', {
        body: { action: 'decrypt', data: encryptedData }
      });
      
      if (error) throw error;
      return result.result;
    } catch (error) {
      console.error('Decryption failed:', error);
      return encryptedData; // Fallback to original
    }
  }, []);

  const setSecureItem = useCallback(async (key: string, value: string) => {
    try {
      const encrypted = await encryptData(value);
      localStorage.setItem(key, encrypted);
    } catch (error) {
      console.error('Secure storage set failed:', error);
      localStorage.setItem(key, value); // Fallback
    }
  }, [encryptData]);

  const getSecureItem = useCallback(async (key: string): Promise<string | null> => {
    try {
      const item = localStorage.getItem(key);
      if (!item) return null;
      return await decryptData(item);
    } catch (error) {
      console.error('Secure storage get failed:', error);
      return localStorage.getItem(key); // Fallback
    }
  }, [decryptData]);

  const removeSecureItem = useCallback((key: string) => {
    localStorage.removeItem(key);
  }, []);

  return {
    setSecureItem,
    getSecureItem,
    removeSecureItem,
  };
};